import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsDashboard() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Dashboard Analytics
                    </h1>
                    <p className="text-xl text-gray-600">
                        Visión analítica completa de los KPIs del negocio con visualizaciones gráficas interactivas.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder al Dashboard Analytics:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, selecciona <strong>"Analytics"</strong></li>
                        <li>Haz clic en <strong>"Dashboard Analytics"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/statistics/dashboard</code></li>
                    </ol>
                </div>

                {/* Selección de Marca */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Seleccionar Marca
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Al acceder al Dashboard Analytics, lo primero que verás es un selector de marca en la parte superior de la página.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                <strong>¿Qué hace?</strong>
                            </p>
                            <p className="text-sm text-gray-700">
                                Permite filtrar todas las métricas y gráficos para mostrar solo los datos de la marca seleccionada. Si tienes múltiples marcas, podrás analizar cada una por separado.
                            </p>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                            <p>
                                <strong>Tip:</strong> Si solo tienes una marca, se seleccionará automáticamente y verás los datos inmediatamente.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Gráficos Disponibles */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Gráficos y Métricas Disponibles
                    </h2>
                    
                    {/* Ventas Totales Mensuales */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-600" />
                            Ventas Totales Mensuales
                        </h3>
                        <p className="text-gray-700 mb-4">
                            Gráfico de barras que muestra la evolución de las ventas totales mes a mes.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Información mostrada:</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                <li>Mes del período (formato: YYYY-MM)</li>
                                <li>Total de ventas del mes en euros</li>
                                <li>Evolución comparativa mes a mes</li>
                            </ul>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                            <p>
                                <strong>Dato útil:</strong> Este gráfico te ayuda a identificar tendencias estacionales y períodos de mayor actividad comercial.
                            </p>
                        </div>
                    </div>

                    {/* Nuevos Clientes Mensuales */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-600" />
                            Nuevos Clientes Mensuales
                        </h3>
                        <p className="text-gray-700 mb-4">
                            Gráfico de barras que muestra cuántos nuevos clientes se registraron cada mes.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Información mostrada:</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                <li>Mes del período</li>
                                <li>Cantidad de nuevos clientes registrados</li>
                                <li>Tendencia de crecimiento de la base de clientes</li>
                            </ul>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                            <p>
                                <strong>Dato útil:</strong> Compara este gráfico con tus campañas de marketing para evaluar su efectividad en la captación de nuevos clientes.
                            </p>
                        </div>
                    </div>

                    {/* Tasa de Recompra */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-orange-600" />
                            Tasa de Recompra Mensual
                        </h3>
                        <p className="text-gray-700 mb-4">
                            Gráfico de área que muestra el porcentaje de clientes que realizan compras repetidas cada mes.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Información mostrada:</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                <li>Mes del período</li>
                                <li>Porcentaje de recompra (0% a 100%)</li>
                                <li>Evolución de la fidelización de clientes</li>
                            </ul>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                            <p>
                                <strong>Dato útil:</strong> Una tasa de recompra alta indica clientes satisfechos y un programa de fidelización efectivo.
                            </p>
                        </div>
                    </div>

                    {/* Ticket Promedio Mensual */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-600" />
                            Ticket Promedio Mensual
                        </h3>
                        <p className="text-gray-700 mb-4">
                            Gráfico de barras que muestra el valor promedio de cada transacción por mes.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Información mostrada:</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                <li>Mes del período</li>
                                <li>Valor promedio del ticket en euros</li>
                                <li>Tendencia del gasto promedio por cliente</li>
                            </ul>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                            <p>
                                <strong>Dato útil:</strong> Un ticket promedio creciente puede indicar estrategias de upselling exitosas o cambios en el comportamiento del cliente.
                            </p>
                        </div>
                    </div>

                    {/* Puntos Calientes del Día */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-purple-600" />
                            Puntos Calientes del Día
                        </h3>
                        <p className="text-gray-700 mb-4">
                            Gráfico de área interactivo que muestra la actividad por horas del día, con selector de día de la semana.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Información mostrada:</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                <li>Hora del día (formato: HH:MM)</li>
                                <li>Nivel de actividad en esa hora</li>
                                <li>Selector para cambiar entre días de la semana (Lunes a Domingo)</li>
                            </ul>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600 mb-4">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                            <p>
                                <strong>Dato útil:</strong> Identifica las horas pico de actividad para optimizar horarios de personal y campañas promocionales.
                            </p>
                        </div>
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Cómo usar el selector:</strong> En la parte superior del gráfico encontrarás un menú desplegable donde puedes seleccionar el día de la semana que deseas analizar. El gráfico se actualizará automáticamente mostrando la actividad de ese día específico.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Interpretación de Datos */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Interpretación de los Datos
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para aprovechar al máximo el Dashboard Analytics, considera estos aspectos:
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Compara períodos:</strong> Observa cómo evolucionan las métricas mes a mes para identificar tendencias.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Identifica correlaciones:</strong> Relaciona los nuevos clientes con tus campañas de marketing y la tasa de recompra con tu programa de fidelización.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Optimiza horarios:</strong> Usa los puntos calientes del día para planificar mejor tus recursos y promociones.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Establece objetivos:</strong> Basa tus metas en los datos históricos mostrados en los gráficos.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No se muestran datos en los gráficos</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de haber seleccionado una marca y de que esa marca tenga datos de ventas y clientes. Si acabas de crear tu cuenta, es normal que los gráficos estén vacíos hasta que tengas transacciones registradas.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Los gráficos muestran datos incorrectos</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que hayas seleccionado la marca correcta en el selector superior. Los datos se filtran automáticamente por la marca seleccionada.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">El selector de día no funciona</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de que tu marca tenga datos de actividad para el día seleccionado. Si no hay datos para ese día específico, el gráfico puede aparecer vacío.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/analytics"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Analytics
                    </Link>
                    <Link
                        to="/documentation/analytics/clientes"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Siguiente: Estadísticas de Clientes
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsDashboard;

